import { inject, Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { SseClient } from 'ngx-sse-client';
import {
  concatMap,
  EMPTY,
  filter,
  Observable,
  of,
  scan,
  throwError,
} from 'rxjs';

import { AdkEvent, AgentStreamPayload } from '../../models/agent-event.model';

export interface ConversationTurn {
  role: 'user' | 'assistant';
  text: string;
}

/**
 * The two ADK event flavors we render: `partial: true` for each streamed
 * token, and a final `partial: false` event carrying the full message.
 *
 * Issue #773 - expand to handle tool calls and thoughts.
 */
interface AdkTextEvent {
  text: string;
  partial: boolean;
}

/**
 * Streams agent replies via SSE.
 * Conversation lifetime is owned by the caller via the `sessionId` argument.
 */
@Injectable({ providedIn: 'root' })
export class AgentChatService {
  private sseClient = inject(SseClient);

  /**
   * Delivers prompt to the server and then for each received SSE, emits the accumulated generated response.
   * The final SSE emission is the authoritative full reply.
   * Errors (transport or in-stream `{detail}` / `{error}` payloads) arrive on the error channel.
   */
  sendPrompt(prompt: string, sessionId: string): Observable<string> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = JSON.stringify({ prompt, sessionId });
    return this.sseClient
      .stream(
        '/agents/run/',
        { keepAlive: false, responseType: 'event' },
        { headers, body },
        'POST',
      )
      .pipe(
        concatMap(toAdkTextEvent),
        scan((acc, evt) => (evt.partial ? acc + evt.text : evt.text), ''),
        filter((accumulated) => accumulated.length > 0),
      );
  }
}

/**
 * Given a Server Sent Event, either creates an `AdkTextEvent` or throws an error.
 * Returns `EMPTY` when the frame has no assistant text to contribute (empty `data`, invalid JSON, parse mismatch) so benign SSE noise does not fail the stream.
 * This behaviour is a defensive default against malformed SSE frames that we don't know how to handle... of which there shouldn't be any but we don't want to break the stream for it.
 */
function toAdkTextEvent(event: Event): Observable<AdkTextEvent> {
  if (event.type === 'error') {
    const err = event as ErrorEvent;
    const statusText = (event as { statusText?: string }).statusText;
    return throwError(
      () => new Error(err.message || statusText || 'SSE transport error'),
    );
  }

  const data = (event as MessageEvent).data;
  if (typeof data !== 'string' || !data) {
    return EMPTY;
  }

  const parsed = AgentStreamPayload.safeParse(safeJsonParse(data));
  if (!parsed.success) {
    return EMPTY;
  }
  const payload = parsed.data;

  if ('detail' in payload) {
    return throwError(() => new Error(payload.detail));
  }
  if ('error' in payload) {
    return throwError(() => new Error(payload.error));
  }

  return modelTextOf(payload);
}

/**
 * Returns the concatenated text of an ADK model event's text parts as an
 * `AdkTextEvent`, or `EMPTY` for non-model events (user echoes, tool calls,
 * thoughts) and model events with no plain text.
 * Issue #773 - expand to handle tool calls and thoughts.
 */
function modelTextOf(payload: AdkEvent): Observable<AdkTextEvent> {
  if (payload.content?.role !== 'model') {
    return EMPTY;
  }
  const text = (payload.content.parts ?? []).map((p) => p.text ?? '').join('');
  if (!text) {
    return EMPTY;
  }
  return of({ text, partial: payload.partial === true });
}

function safeJsonParse(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}
